import { FC, useState, useContext } from 'react'
import { Edge } from '@xyflow/react'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import { INodeData } from '../../../../types/node.type'
import { SpexNode } from '../../../../types/playbook.type'
import { Box, Button, Drawer, IconButton, TextField, Typography } from '@mui/material'
import TextFieldList from './TextFieldList'
import TextFieldPair from './TextFieldPair'
import TextFieldPairList from './TextFieldPairList'
import { PlaybookContext } from '../../../../contexts/playbooks.context'
import { AppConfigContext } from '../../../../contexts/app-config.context'
import { INodeType } from '../../../../../shared/types/api/playbook.type'

import styles from './styles.module.scss'

interface IProps {
  nodeId?: string
  edgeId?: string
  type: string
  label: string
  nodeType: INodeType | null
  defaultData: INodeData | {}
  handleClose: () => void
}

const NodeDrawer: FC<IProps> = ({ nodeId, edgeId, type, label, nodeType, defaultData, handleClose }) => {
  const [data, setData] = useState<any>(defaultData || { label })
  const { edges, reactFlowNodes, spexNodes, playbook, setPlaybook, setEdges, savePlaybook } =
    useContext(PlaybookContext)
  const { isLightMode } = useContext(AppConfigContext)

  const onClose = () => {
    handleClose()
    setData({})
  }

  const handleChange = (key: string, value: string | number | Array<string> | Array<[string, string]>) => {
    setData({ ...data, [key]: value })
  }

  const forceData = (edge: Edge) => {
    return { ...edge, data: edge.data || {} }
  }

  const handleSave = async () => {
    if (!playbook) throw new Error("Can't save without a playbook")

    if (nodeId || edgeId) {
      let newNodes = reactFlowNodes
      let newEdges = edges.map((edge) => forceData(edge))

      if (nodeId) {
        newNodes = reactFlowNodes.map((node) => (node.id === nodeId ? { ...node, data } : node))
      } else if (edgeId) {
        newEdges = edges.map((edge) => forceData(edge.id === edgeId ? { ...edge, data } : edge))
      }

      playbook.nodes = newNodes.map((n) => {
        const n2: SpexNode = spexNodes[parseInt(n.id)]
        if (nodeId && nodeId === n.id) return { ...n2, data }
        else return n2
      })
      playbook.edges = newEdges

      await savePlaybook({ ...playbook })
      onClose()
    }
  }

  const handleDelete = async () => {
    if (!playbook) {
      return
    }

    const newPlaybook = { ...playbook }

    if (nodeId) {
      // Remove any edge connected to the deleted node(s):
      newPlaybook.edges = playbook.edges.filter((edge) => nodeId !== edge.source && nodeId !== edge.target)

      newPlaybook.nodes = playbook.nodes.filter((n) => n.id !== nodeId)
    } else {
      newPlaybook.edges = playbook.edges.filter((edge) => edge.id !== edgeId)
    }

    setEdges(newPlaybook.edges)
    setPlaybook(newPlaybook)

    await savePlaybook(newPlaybook)
    onClose()
  }

  const NodeTypeContent = () => {
    return (
      <Box className={styles.postRequestInfo} sx={{ borderColor: isLightMode ? 'black' : 'white' }}>
        <TextField
          key="label"
          value={data.label || ''}
          onChange={(ev) => handleChange('label', ev.target.value)}
          className={styles.input}
          label="Label"
        />

        {nodeId &&
          nodeType?.params?.map((param, i) => {
            if (param.type === 'text') {
              return (
                <TextField
                  key={i.toString()}
                  value={data[param.slug] || ''}
                  onChange={(ev) => handleChange(param.slug, ev.target.value)}
                  className={styles.input}
                  label={param.label}
                />
              )
            } else if (param.type === 'text[]') {
              return (
                <TextFieldList
                  key={i.toString()}
                  value={data[param.slug] || []}
                  onChange={(val) => handleChange(param.slug, val)}
                  className={styles.input}
                  label={param.label}
                />
              )
            } else if (param.type === 'textarea') {
              return (
                <TextField
                  key={i.toString()}
                  value={data[param.slug] || ''}
                  onChange={(ev) => handleChange(param.slug, ev.target.value)}
                  className={styles.input}
                  label={param.label}
                  multiline
                  minRows={4}
                />
              )
            } else if (param.type === 'pair') {
              return (
                <TextFieldPair
                  key={i.toString()}
                  value={data[param.slug] || ['', '']}
                  onChange={(val) => handleChange(param.slug, val)}
                  className={styles.input}
                  label={param.label}
                />
              )
            } else if (param.type === 'pair[]') {
              return (
                <TextFieldPairList
                  key={i.toString()}
                  value={data[param.slug] || []}
                  onChange={(val) => handleChange(param.slug, val)}
                  className={styles.input}
                  label={param.label}
                />
              )
            } else if (param.type === 'number') {
              return (
                <TextField
                  key={i.toString()}
                  value={data[param.slug] || ''}
                  onChange={(ev) => handleChange(param.slug, parseFloat(ev.target.value))}
                  className={styles.input}
                  label={param.label}
                  inputProps={{ type: 'number' }}
                />
              )
            } else {
              return null
            }
          })}
      </Box>
    )
  }

  return (
    <Drawer
      className={styles.drawer}
      anchor="right"
      open
      onClose={onClose}
      classes={{
        paper: styles.drawerPaper,
      }}
    >
      <Box className={styles.drawerHeader}>
        <Typography variant="h5">Edit {edgeId ? 'Edge' : 'Node'}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon className={styles.closeIcon} />
        </IconButton>
      </Box>
      <Box className={styles.drawerContent}>{NodeTypeContent()}</Box>
      <Box className={styles.actions}>
        <Button onClick={handleDelete} startIcon={<DeleteIcon />}>
          Delete
        </Button>
        <Box className={styles.actionGroup}>
          <Button onClick={onClose} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}

export default NodeDrawer
